import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    InlineStack,
    Page,
    Text,
} from '@shopify/polaris';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const productLinkQueryKeys = new Set([
    // Shopify Admin links can provide the selected product id under different
    // query-string keys depending on target/API behavior, so accept the known
    // variants and normalize them below.
    'id',
    'ids',
    'ids[]',
    'product',
    'product_id',
    'productId',
    'product_gid',
    'productGid',
    'admin_graphql_api_id',
    'resourceId',
    'resource_id',
    'resourceIds',
    'resource_ids',
    'selected',
    'selection',
    'gid',
    'shopifyProductId',
    'shopify_product_id',
    'shopifyProductGid',
    'shopify_product_gid',
].map((key) => key.toLowerCase()));

const trimLinkToken = (value: string) => {
    // Some Admin link payloads look like arrays or quoted values. Trim only the
    // wrapper characters so a raw numeric id or Shopify GID can be parsed.
    const trimCharacters = new Set(['[', ']', '"', "'"]);
    let result = value.trim();

    while (result.length > 0 && trimCharacters.has(result[0])) {
        result = result.substring(1).trimStart();
    }

    while (result.length > 0 && trimCharacters.has(result[result.length - 1])) {
        result = result.substring(0, result.length - 1).trimEnd();
    }

    return result;
};

const extractProductId = (search: string) => {
    const params = new URLSearchParams(search);
    let productId = '';

    params.forEach((value, key) => {
        if (productId || !productLinkQueryKeys.has(key.toLowerCase()) || !value) {
            return;
        }

        for (const token of value.split(',')) {
            const normalized = trimLinkToken(token);
            if (!normalized) {
                continue;
            }

            const productGidPrefix = 'gid://shopify/Product/';
            const gidIndex = normalized.toLowerCase().indexOf(productGidPrefix.toLowerCase());
            // Support both plain numeric ids and Shopify product GIDs:
            // gid://shopify/Product/10664940896568.
            const candidate = gidIndex >= 0
                ? normalized.substring(gidIndex + productGidPrefix.length)
                : normalized.substring(normalized.lastIndexOf('/') + 1);

            if (/^\d+$/.test(candidate)) {
                productId = candidate;
                return;
            }
        }
    });

    return productId;
};

const ProductAdminLinkRedirectPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const productId = useMemo(() => extractProductId(location.search), [location.search]);

    useEffect(() => {
        if (productId) {
            // Replace the temporary Shopify Admin link route with the real
            // in-app product details page where fitments are managed.
            navigate(`/products/${productId}`, { replace: true });
        }
    }, [navigate, productId]);

    return (
        <Page title="Manage product fitments" backAction={{ content: 'Products', onAction: () => navigate('/products') }}>
            <Card>
                <Box padding="400">
                    {productId ? (
                        <Text as="p">Opening product fitments...</Text>
                    ) : (
                        <BlockStack gap="300">
                            <Banner tone="warning" title="Product not found">
                                <p>Shopify did not provide a product identifier for this link.</p>
                            </Banner>
                            <InlineStack gap="200">
                                <Button onClick={() => navigate('/products')}>Open products</Button>
                            </InlineStack>
                        </BlockStack>
                    )}
                </Box>
            </Card>
        </Page>
    );
};

export default ProductAdminLinkRedirectPage;
